import type { NorfolkSouthernTrainSelection } from '@rsos/reduxDataStore/src/features/norfolkSouthern/types';
import type {
  ConsistCar,
  ConsistRequester,
  NorfolkSouthernRequestResponse,
  NorfolkSouthernTrainSnapshot,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import type { ResourceRequestQueueCategory } from '../adapters/types';
import { RESOURCE_REQUEST_ACTIVE_TIMEOUT_MS } from '../queueRetention';

export const EMPTY_VALUE = '—';

export const CONSIST_COMPLETED_LABEL = 'Consist Completed';
export const CONSIST_FAILED_LABEL = 'Consist Failed';

export type ConsistChipTone = 'success' | 'error';

function parseTimestampMs(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Latest activity for a consist request, in epoch ms. Norfolk Southern
 * sends no status stream, so this is always the request's creation time.
 */
export function getNorfolkSouthernLatestActivityMs(
  request: NorfolkSouthernRequestResponse,
): number {
  return parseTimestampMs(request.createdAt);
}

/**
 * Queue section for a consist request. Closed when the user closed it or it
 * has aged out; otherwise a failed fetch sits in Error so it stands out,
 * and a completed consist is Active.
 */
export function getNorfolkSouthernQueueCategory(
  request: NorfolkSouthernRequestResponse,
  nowMs: number,
): ResourceRequestQueueCategory {
  if (request.queueState === 'closed') {
    return 'closed';
  }
  if (
    nowMs - getNorfolkSouthernLatestActivityMs(request) >=
    RESOURCE_REQUEST_ACTIVE_TIMEOUT_MS
  ) {
    return 'closed';
  }
  return request.request.consistState === 'failed' ? 'error' : 'active';
}

/** Status chip for a consist request: its terminal state. */
export function resolveNorfolkSouthernStatusChip(
  request: NorfolkSouthernRequestResponse,
): { display: string; tone: ConsistChipTone } {
  return request.request.consistState === 'failed'
    ? { display: CONSIST_FAILED_LABEL, tone: 'error' }
    : { display: CONSIST_COMPLETED_LABEL, tone: 'success' };
}

export function formatRequesterName(requester: ConsistRequester): string {
  const name = [requester.firstName, requester.lastName]
    .filter(part => part && part.trim().length > 0)
    .join(' ');
  return name || requester.email;
}

/** Cars flagged hazmat, on the car itself or in a container it carries. */
export function getHazmatCars(cars: readonly ConsistCar[]): ConsistCar[] {
  return cars.filter(car => car.hazmat);
}

/** "CAR 4 - DTTX 621012" style heading for a car row. */
export function formatCarHeading(car: ConsistCar): string {
  return typeof car.sequence === 'number'
    ? `Car ${car.sequence} - ${car.reportingMarks}`
    : car.reportingMarks;
}

/**
 * The train recorded on a consist request, as a selection the form can
 * reuse for a retry. Route context is not persisted with the request.
 */
export function trainSelectionFromSnapshot(
  snapshot: NorfolkSouthernTrainSnapshot,
): NorfolkSouthernTrainSelection {
  return {
    externalId: snapshot.externalId,
    trainId: snapshot.trainId,
    trainSymbol: snapshot.trainSymbol ?? null,
    position: snapshot.position,
    speedMph: snapshot.speedMph ?? null,
    headingDegrees: snapshot.headingDegrees ?? null,
    numberOfCars: snapshot.numberOfCars ?? null,
    trainLengthFeet: snapshot.trainLengthFeet ?? null,
    status: snapshot.status ?? null,
    originStation: null,
    nextStation: null,
    destinationStation: null,
  };
}
